
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import { Section } from './types';
import { pathToSection, sectionToPath } from './routing';
import MobileNav from './components/MobileNav';
import NotFound from './components/NotFound';
import ProjectDetailOverlay from './components/ProjectDetailOverlay';
import ProjectDetailRoute from './components/ProjectDetailRoute';
import SectionContent from './components/SectionContent';

const App: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const activeSection = useMemo(() => pathToSection(location.pathname), [location.pathname]);
  const [previousSection, setPreviousSection] = useState<Section>(activeSection);
  const previousSectionRef = useRef<Section>(activeSection);

  useEffect(() => {
    setPreviousSection(previousSectionRef.current);
    previousSectionRef.current = activeSection;
  }, [activeSection]);

  const handleSectionChange = (section: Section) => {
    navigate(sectionToPath[section]);
  };

  return (
    <div className="flex flex-col md:flex-row h-screen bg-black text-white overflow-hidden p-4 md:p-8 selection:bg-red-500 selection:text-white">
      {/* App Shell */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden rounded-[2rem] md:rounded-[2.5rem] border border-white/5 glass-panel shadow-2xl relative">

        {/* Desktop Sidebar */}
        <Sidebar activeSection={activeSection} onSectionChange={handleSectionChange} />

        <main className="flex-1 relative overflow-hidden flex flex-col bg-zinc-950/40">
          {/* High-fidelity background accents */}
          <div className="absolute top-[-20%] left-[-10%] w-[80%] md:w-[60%] h-[60%] bg-red-600/10 blur-[100px] md:blur-[150px] rounded-full pointer-events-none"></div>
          <div className="absolute bottom-[-10%] right-[10%] w-[50%] md:w-[40%] h-[40%] bg-blue-600/5 blur-[80px] md:blur-[120px] rounded-full pointer-events-none"></div>

          {/* Section Container */}
          <div
            style={{ paddingBottom: 'calc(8rem + env(safe-area-inset-bottom))' }}
            className="flex-1 overflow-y-auto px-6 md:px-12 py-8 md:py-12 transition-all duration-500 ease-out no-scrollbar"
          >
            <Routes>
              <Route
                path="/work/:projectId"
                element={
                  <>
                    <div key={activeSection} className="animate-fadeInUp">
                      <SectionContent
                        activeSection={activeSection}
                        previousSection={previousSection}
                        onSectionChange={handleSectionChange}
                      />
                    </div>
                    <ProjectDetailRoute render={(project) => <ProjectDetailOverlay project={project} />} />
                  </>
                }
              />
              <Route
                path="/"
                element={
                  <div key={activeSection} className="animate-fadeInUp">
                    <SectionContent
                      activeSection={activeSection}
                      previousSection={previousSection}
                      onSectionChange={handleSectionChange}
                    />
                  </div>
                }
              />
              <Route
                path="/work"
                element={
                  <div key={activeSection} className="animate-fadeInUp">
                    <SectionContent
                      activeSection={activeSection}
                      previousSection={previousSection}
                      onSectionChange={handleSectionChange}
                    />
                  </div>
                }
              />
              <Route
                path="/skills"
                element={
                  <div key={activeSection} className="animate-fadeInUp">
                    <SectionContent
                      activeSection={activeSection}
                      previousSection={previousSection}
                      onSectionChange={handleSectionChange}
                    />
                  </div>
                }
              />
              <Route
                path="/about"
                element={
                  <div key={activeSection} className="animate-fadeInUp">
                    <SectionContent
                      activeSection={activeSection}
                      previousSection={previousSection}
                      onSectionChange={handleSectionChange}
                    />
                  </div>
                }
              />
              <Route
                path="/contact"
                element={
                  <div key={activeSection} className="animate-fadeInUp">
                    <SectionContent
                      activeSection={activeSection}
                      previousSection={previousSection}
                      onSectionChange={handleSectionChange}
                    />
                  </div>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </div>

          {/* Minimal footer line (hidden on mobile to save space) */}
          <div className="hidden md:flex h-16 items-center px-12 md:px-24 border-t border-white/5 text-[10px] text-zinc-700 font-mono tracking-widest uppercase">
            <span>Sanket © 2025 — Design x Engineering</span>
          </div>
        </main>
      </div>

      <MobileNav activeSection={activeSection} onSectionChange={handleSectionChange} />
    </div>
  );
};

export default App;
